import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  IvyLeagueAdminConfiguration,
  IvyLeagueChild,
  IvyLeagueFamilyInfo,
  Kid,
  PricingConfig,
  TransportMode
} from '../../types/ivyLeague'

interface IvyLeagueInitialState {
  step: number
  children: string[]
  kids: Kid[]
  childrenData: IvyLeagueChild[]
  familyInfo: IvyLeagueFamilyInfo
  pricingConfig: PricingConfig | null
  adminConfiguration: IvyLeagueAdminConfiguration | undefined
}

const defaultFamilyInfo: IvyLeagueFamilyInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  secondaryPhone: '',
  address: '',
  isTeacherFamily: false,
  occupationalDiscountType: 'None',
  isAlumniFamily: false,
  hasReferralDiscount: false,
  paymentDiscountType: 'NONE',
  paymentMethod: 'Other'
}

const initialState: IvyLeagueInitialState = {
  step: 1,
  children: [],
  kids: [], // use Kids in future and remove children, but for now keep both to avoid bugs since children is used in multiple places
  childrenData: [],
  familyInfo: defaultFamilyInfo,
  pricingConfig: null,
  adminConfiguration: undefined
}

const setKids = (state = initialState, action: PayloadAction<Kid[]>) => {
  const normalizedKids: Kid[] = (action.payload ?? []).map((k) => ({
    ...k,
    programs: k.programs ?? [],
    transportMode: k.transportMode ?? 'Parent',
    usesExtendedHours: k.usesExtendedHours ?? false,
    finaleTrip: k.finaleTrip ?? false
  }))
  return {
    ...state,
    kids: normalizedKids
  }
}

const setKidGrade = (state = initialState, action: PayloadAction<{ kidId: string; grade: string }>) => {
  const { kidId, grade } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state
  if (existingKid.grade === grade) return state

  const updatedKids = state.kids.map((k) => {
    if (k.id === kidId) {
      return {
        ...k,
        grade,
        programs: [{
          programId: 'program-1',
          programType: '',
          programName: '',
          weekCount: 0
        }]
      }
    }
    return k
  })
  return {
    ...state,
    kids: updatedKids
  }
}

const setKidTransportMode = (
  state = initialState,
  action: PayloadAction<{ kidId: string; transportMode: TransportMode }>
) => {
  const { kidId, transportMode } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state
  if ((existingKid.transportMode ?? 'Parent') === transportMode) return state

  const updatedKids = state.kids.map((k) => (
    k.id === kidId
      ? { ...k, transportMode }
      : k
  ))
  return { ...state, kids: updatedKids }
}

const setKidUsesExtendedHours = (
  state = initialState,
  action: PayloadAction<{ kidId: string; usesExtendedHours: boolean }>
) => {
  const { kidId, usesExtendedHours } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state
  if ((existingKid.usesExtendedHours ?? false) === usesExtendedHours) return state

  const updatedKids = state.kids.map((k) => (
    k.id === kidId
      ? { ...k, usesExtendedHours }
      : k
  ))
  return { ...state, kids: updatedKids }
}

const setKidFinaleTrip = (
  state = initialState,
  action: PayloadAction<{ kidId: string; finaleTrip: boolean }>
) => {
  const { kidId, finaleTrip } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state
  if ((existingKid.finaleTrip ?? false) === finaleTrip) return state

  const updatedKids = state.kids.map((k) => (
    k.id === kidId
      ? { ...k, finaleTrip }
      : k
  ))
  return { ...state, kids: updatedKids }
}

const setProgramTypeOfaKid = (state = initialState, action: PayloadAction<{ kidId: string; programId: string; programType: string }>) => {
  const { kidId, programId, programType } = action.payload

  const existingKid = state.kids.find((k) => k.id === kidId)
  const existingProgram = existingKid?.programs?.find((p) => p.programId === programId)
  if (!existingKid || !existingProgram) return state
  if (existingProgram.programType === programType) return state

  const updatedKids = state.kids.map((k) => {
    if (k.id === kidId) {
      const updatedPrograms = k.programs.map((p) => {
        if (p.programId === programId) {
          return {
            ...p,
            programType,
            programName: '',
            weekCount: 0
          }
        }
        return p
      })
      return {
        ...k,
        programs: updatedPrograms
      }
    }
    return k
  })
  return {
    ...state,
    kids: updatedKids
  }
}

const setProgramNameOfaKid = (state = initialState, action: PayloadAction<{ kidId: string; programId: string; programName: string }>) => {
  const { kidId, programId, programName } = action.payload

  const existingKid = state.kids.find((k) => k.id === kidId)
  const existingProgram = existingKid?.programs?.find((p) => p.programId === programId)
  if (!existingKid || !existingProgram) return state
  if (existingProgram.programName === programName) return state

  const updatedKids = state.kids.map((k) => {
    if (k.id === kidId) {
      const updatedPrograms = k.programs.map((p) => {
        if (p.programId === programId) {
          return {
            ...p,
            programName,
            weekCount: 0
          }
        }
        return p
      })
      return {
        ...k,
        programs: updatedPrograms
      }
    }
    return k
  })
  return {
    ...state,
    kids: updatedKids
  }
}

const setWeekCountOfaKid = (state = initialState, action: PayloadAction<{ kidId: string; programId: string; weekCount: number }>) => {
  const { kidId, programId, weekCount } = action.payload

  const existingKid = state.kids.find((k) => k.id === kidId)
  const existingProgram = existingKid?.programs?.find((p) => p.programId === programId)
  if (!existingKid || !existingProgram) return state
  if (existingProgram.weekCount === weekCount) return state

  const updatedKids = state.kids.map((k) => {
    if (k.id === kidId) {
      const updatedPrograms = k.programs.map((p) => {
        if (p.programId === programId) {
          return {
            ...p,
            weekCount
          }
        }
        return p
      })
      return {
        ...k,
        programs: updatedPrograms
      }
    }
    return k
  })
  return {
    ...state,
    kids: updatedKids
  }
}

const renumberPrograms = (programs: Kid['programs']): Kid['programs'] => {
  return programs.map((p, idx) => ({
    ...p,
    programId: `program-${idx + 1}`
  }))
}

const addProgramOfaKid = (state = initialState, action: PayloadAction<{ kidId: string; programType?: string }>) => {
  const { kidId, programType } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state

  const currentPrograms = existingKid.programs ?? []
  if (currentPrograms.length >= 2) return state

  const normalizedType = programType ?? ''
  if (normalizedType && currentPrograms.some((p) => p.programType === normalizedType)) return state

  const nextPrograms = renumberPrograms([
    ...currentPrograms,
    {
      programId: 'program-temp',
      programType: normalizedType,
      programName: '',
      weekCount: 0
    }
  ])

  const updatedKids = state.kids.map((k) => (k.id === kidId ? { ...k, programs: nextPrograms } : k))
  return { ...state, kids: updatedKids }
}

const removeProgramOfaKid = (state = initialState, action: PayloadAction<{ kidId: string; programId: string }>) => {
  const { kidId, programId } = action.payload
  const existingKid = state.kids.find((k) => k.id === kidId)
  if (!existingKid) return state

  const currentPrograms = existingKid.programs ?? []
  if (currentPrograms.length <= 1) return state
  if (!currentPrograms.some((p) => p.programId === programId)) return state

  const remaining = currentPrograms.filter((p) => p.programId !== programId)
  const nextPrograms = renumberPrograms(remaining)

  const updatedKids = state.kids.map((k) => (k.id === kidId ? { ...k, programs: nextPrograms } : k))
  return { ...state, kids: updatedKids }
}

const ivyLeagueSlice = createSlice({
  name: 'ivyLeague',
  initialState,
  reducers: {
    setStepNumber: (state, action: PayloadAction<number>) => {
      state.step = action.payload
    },
    setChildren: (state, action: PayloadAction<string[]>) => {
      state.children = action.payload
    },
    setChildrenData: (state, action: PayloadAction<IvyLeagueChild[]>) => {
      state.childrenData = action.payload
    },
    setFamilyInfo: (state, action: PayloadAction<IvyLeagueFamilyInfo>) => {
      state.familyInfo = action.payload
    },
    setPricingConfig: (state, action: PayloadAction<PricingConfig | null>) => {
      state.pricingConfig = action.payload
    },
    setAdminConfiguration: (state, action: PayloadAction<IvyLeagueAdminConfiguration | undefined>) => {
      state.adminConfiguration = action.payload
    },
    setKids,
    setKidGrade,
    setKidTransportMode,
    setKidUsesExtendedHours,
    setKidFinaleTrip,
    setProgramTypeOfaKid,
    setProgramNameOfaKid,
    setWeekCountOfaKid,
    addProgramOfaKid,
    removeProgramOfaKid
  }
})

export default ivyLeagueSlice
